/**
 * Section contract for the STO details page.
 *
 * A section renders iff it is declared visible in back-office AND has renderable
 * content. `data` is only reachable in the `ready` branch, so a section cannot be
 * written without handling the hidden and loading cases.
 *
 * See docs/STO-ARCHITECTURE.md §4.
 */

export type SectionState<T> = { status: 'hidden' } | { status: 'loading' } | { status: 'ready'; data: T }

export interface ResolveSectionInput<T> {
  /** Back-office `isTabVisible`. Undefined means the section has no such flag. */
  declaredVisible?: boolean
  isPending: boolean
  isError?: boolean
  data: T | undefined
  /** Content-specific emptiness test. An intentional "no data" message is NOT empty. */
  isEmpty: (data: T) => boolean
}

const HIDDEN = { status: 'hidden' } as const
const LOADING = { status: 'loading' } as const

export function resolveSectionState<T>({
  declaredVisible,
  isPending,
  isError,
  data,
  isEmpty,
}: ResolveSectionInput<T>): SectionState<T> {
  // Retries are already exhausted by the shared query client (3 attempts, 4xx
  // excluded), so a failed section is omitted rather than shown as an error.
  if (isError) return HIDDEN
  if (isPending) return LOADING
  if (declaredVisible === false) return HIDDEN
  if (data === undefined || data === null) return HIDDEN
  if (isEmpty(data)) return HIDDEN
  return { status: 'ready', data }
}

/** Convenience for the common `array.length === 0` case. */
export const isEmptyList = (list: readonly unknown[] | undefined | null): boolean => !list || list.length === 0
